import { useState } from 'react';

import {
    Carousel,
    CarouselContent,
    CarouselItem,
} from '@rocco/components/carousel';
import { useCarousel } from '@rocco/components/carousel/carousel';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import { SpecifierLoginPrompt } from '~/modules/enquiry/component/specifier-login-prompt/SpecifierLoginPrompt';
import {
    detailCta,
    RICH_TEXT_HTML_CLASS,
} from '~/ui-rocco/pages/detail-shared';
import { SectionSpacer } from '~/ui-rocco/pages/home-page/components/SectionSpacer';
import { CarouselPaginationPill } from '~/ui-rocco/pages/home-page/components/CarouselPaginationPill';
import { useProfessionalOverviewShared } from '@modules/professional/hook/use-professional-overview-shared';
import { ClientDirectorBookingModal } from '~/modules-rocco/booking';
import { getFeaturedWork } from '../utils/featured-work';
import { canBookDirectMeeting } from '../utils/book-meeting';

/**
 * Fallback copy used until the professional customises the section — also the
 * content shown while verifying the layout (Figma §9). Real CMS/BM values
 * override each field/list individually.
 */
const PLACEHOLDER_EYEBROW = '(Get in touch)';
const PLACEHOLDER_HEADLINE = 'Talk to us about your Project';
const PLACEHOLDER_INTRO =
    "Define your vision. Browse New Zealand's largest selection of completed architectural projects and connect with the professionals behind every build.";
const PLACEHOLDER_PROCESS_HEADING = 'Our Process';

const PLACEHOLDER_BENEFITS = [
    {
        title: 'Save time',
        body: "Find everything you need to build, whether it's a tap or a cladding system. Connect to purchase directly from New Zealand's leading product suppliers.",
    },
    {
        title: 'Save time',
        body: "Find everything you need to build, whether it's a tap or a cladding system. Connect to purchase directly from New Zealand's leading product suppliers.",
    },
    {
        title: 'Save time',
        body: "Find everything you need to build, whether it's a tap or a cladding system. Connect to purchase directly from New Zealand's leading product suppliers.",
    },
    {
        title: 'Save time',
        body: "Find everything you need to build, whether it's a tap or a cladding system. Connect to purchase directly from New Zealand's leading product suppliers.",
    },
];

const PLACEHOLDER_STEPS = [
    { body: 'Outline your building project goals and requirements.' },
    {
        body: 'Gather your project specifications. Having your budget and timeline prepared helps professionals provide accurate quotes.',
    },
    {
        body: 'Choose the right professional for your project. Consider profile aspects like location, design aesthetic, experience and reviews.',
    },
];

/**
 * Desktop-only size override so this section's two CTAs match the larger Figma
 * buttons (60px tall · 22.7px medium · -0.68px · 230px min · 27.6px px) rather
 * than the standard 52px/16px {@link detailCta}. Mobile keeps the shared size.
 */
const CONTACT_CTA_SIZE =
    'md:h-[3.75rem] md:min-w-[14.39rem] md:px-[1.723rem] md:text-[1.42rem] md:font-normal md:tracking-[-0.03em]';

interface SectionImage {
    id: number;
    src?: string;
    alt: string;
    link?: string;
}

/** Two-digit process-step number derived from position (01, 02, …). */
const stepNumber = (index: number): string =>
    String(index + 1).padStart(2, '0');

/** Dark pagination pill under the image carousel (self-hides when it fits). */
const CarouselPagination = () => {
    const {
        api,
        currentSlide,
        totalSlides,
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
    } = useCarousel();

    if (!canScrollPrev && !canScrollNext) return null;

    return (
        <div className="flex justify-center max-md:pt-8 md:pt-[3rem]">
            <CarouselPaginationPill
                total={totalSlides}
                activeIndex={currentSlide}
                onPrev={scrollPrev}
                onNext={scrollNext}
                onSelect={(index) => api?.scrollTo(index)}
                label="Project images"
                theme="dark"
            />
        </div>
    );
};

/** A single §9 image tile (aspect 751/619), wrapped in a link when set. */
const SectionImageTile = ({ image }: { image: SectionImage }) => {
    const tile = (
        <div className="bg-neutral-800 aspect-[751/619] overflow-hidden">
            <img
                src={image.src}
                alt={image.alt}
                loading="lazy"
                className="size-full object-cover"
            />
        </div>
    );

    return image.link ? (
        <RoccoLink to={image.link} className="block">
            {tile}
        </RoccoLink>
    ) : (
        tile
    );
};

interface ProfessionalContactSectionProps {
    /** Leading white space above the dark section — folded into the component so
     *  it disappears with the section when hidden. Each page passes its own gap. */
    leadingSpacerHeight?: number;
    leadingSpacerMobileHeight?: number;
    leadingSpacerClassName?: string;
}

/**
 * §9 — dark "Talk to us about your project" section. Premium + editable: hidden
 * unless the professional is entitled and has enabled it. Sizes/spacing mirror
 * the Figma 1:1 (design px ÷ 16 → rem): right-aligned eyebrow, a 200px heading,
 * a right-offset column (intro + a 2×2 benefits grid + two CTAs), an image
 * carousel, the "Our Process" steps, and a wide featured (hanging) closing
 * image. Content comes from the professional's configured fields, falling back
 * to the placeholder copy + featured-project images when a field is empty.
 */
export const ProfessionalContactSection = ({
    leadingSpacerHeight = 160,
    leadingSpacerMobileHeight = 48,
    leadingSpacerClassName = 'bg-white',
}: ProfessionalContactSectionProps = {}) => {
    const { professionalOverview, featuredProjects, associatedProjects } =
        useProfessionalOverviewShared();
    const professional = useProfessionalInitialRouteData();
    const { showEnquiryCTA, showSpecifierLoginPrompt } =
        useEnquiryVisibility(professional);
    const { openEnquiryModal } = useEnquiryModal({
        professional,
        itemType: 'professional',
        siteTreeID: professional.ID,
        itemTitle: professional.Title,
        includeBranch: true,
    });
    const bookingSession = professionalOverview.ClientDirectorCalendarSession;
    const canBookMeeting = canBookDirectMeeting(bookingSession);
    const [bookingOpen, setBookingOpen] = useState(false);

    // Premium gate — hidden unless entitled (add-on / feature flag) AND enabled.
    if (
        !professionalOverview.ContactSectionEnabled ||
        !professionalOverview.ContactSectionEntitled
    ) {
        return null;
    }

    // Product suppliers (e.g. Resene) have no projects of their own — fall back
    // to associated projects so the image slots aren't empty.
    const fallbackWork = getFeaturedWork(
        featuredProjects.featuredProjectsList,
        associatedProjects,
    );

    const eyebrow = professionalOverview.ContactEyebrow || PLACEHOLDER_EYEBROW;
    const headline =
        professionalOverview.ContactHeadline || PLACEHOLDER_HEADLINE;
    const intro = professionalOverview.ContactIntro || PLACEHOLDER_INTRO;
    const processHeading =
        professionalOverview.ContactProcessHeading ||
        PLACEHOLDER_PROCESS_HEADING;

    const benefits = professionalOverview.ContactBenefits.length
        ? professionalOverview.ContactBenefits.map((benefit) => ({
              title: benefit.Title ?? '',
              body: benefit.Body ?? '',
          }))
        : PLACEHOLDER_BENEFITS;

    const steps = professionalOverview.ContactSteps.length
        ? professionalOverview.ContactSteps.map((step) => ({
              body: step.Description ?? '',
          }))
        : PLACEHOLDER_STEPS;

    // Curated CMS images, keeping only those that resolve to a usable URL, so a
    // set of unresolvable rows falls back to featured work rather than blanking.
    const cmsCarousel: SectionImage[] =
        professionalOverview.ContactCarouselImages.map((item) => ({
            id: item.ID,
            src:
                item.ContactImage?.LibraryImageFile?.DetailPageStandard
                    ?.Desktop ?? undefined,
            alt: item.ContactImage?.Caption ?? '',
        })).filter((image) => image.src);

    const cmsFeaturedImage: SectionImage | null =
        professionalOverview.ContactFeaturedImage
            ? {
                  id: professionalOverview.ContactFeaturedImage.ID,
                  src:
                      professionalOverview.ContactFeaturedImage.LibraryImageFile
                          ?.DetailPageStandard?.Desktop ?? undefined,
                  alt: professionalOverview.ContactFeaturedImage.Caption ?? '',
              }
            : null;

    // The lead project is only skipped when it's also the hanging featured
    // image, so a CMS featured image leaves it free for the carousel.
    const carouselStart = cmsFeaturedImage?.src ? 0 : 1;
    const fallbackCarousel: SectionImage[] = fallbackWork
        .slice(carouselStart, carouselStart + 6)
        .map((project) => ({
            id: project.id,
            src: project.images?.[0]?.src,
            alt: project.title,
            link: project.link,
        }));

    const visibleCarousel = (
        cmsCarousel.length ? cmsCarousel : fallbackCarousel
    ).filter((image) => image.src);

    const fallbackFeaturedImage: SectionImage | null = fallbackWork[0]
        ? {
              id: fallbackWork[0].id,
              src: fallbackWork[0].images?.[0]?.src,
              alt: fallbackWork[0].title,
              link: fallbackWork[0].link,
          }
        : null;

    // Fall back when the CMS featured image is unset or doesn't resolve.
    const featuredImage = cmsFeaturedImage?.src
        ? cmsFeaturedImage
        : fallbackFeaturedImage;

    return (
        <>
            {leadingSpacerHeight > 0 && (
                <SectionSpacer
                    height={leadingSpacerHeight}
                    mobileHeight={leadingSpacerMobileHeight}
                    className={leadingSpacerClassName}
                />
            )}
            <section className="relative z-10 w-full overflow-x-clip bg-[#181818] text-white max-md:px-6 max-md:py-12 md:px-[3rem] md:py-[9.9rem]">
                <div className="mx-auto w-full max-w-[114rem]">
                    {/* Eyebrow — right column, hairline above (Figma top-right). */}
                    <div className="flex md:justify-end">
                        <div className="w-full md:w-[49.4%]">
                            <div className="h-px w-full bg-white/40" />
                            <p className="m-0 font-sans font-normal text-white max-md:mt-3 max-md:text-sm md:mt-[1.0625rem] md:text-[2rem] md:leading-[2.5rem] md:tracking-[-0.02rem]">
                                {eyebrow}
                            </p>
                        </div>
                    </div>

                    <h2 className="m-0 font-sans font-normal text-white max-md:mt-6 max-md:text-[2rem] max-md:leading-[1.08] max-md:tracking-[-0.02em] md:mt-[11.9rem] md:text-[12.5rem] md:leading-[11.25rem] md:tracking-[-0.125rem]">
                        {headline}
                    </h2>

                    {/* Right-offset content column — intro + benefits grid + CTAs. */}
                    <div className="flex flex-col max-md:mt-8 md:mt-[13.3rem] md:ml-[43.1%] md:w-[52.8%]">
                        <div
                            className={cn(
                                'm-0 font-sans font-normal text-white max-md:text-base md:max-w-[56rem] md:text-[2.4375rem] md:leading-[1.3]',
                                RICH_TEXT_HTML_CLASS,
                            )}
                            dangerouslySetInnerHTML={{ __html: intro }}
                        />

                        <div className="grid grid-cols-1 max-md:mt-8 max-md:gap-y-6 sm:grid-cols-2 md:mt-[10.9375rem] md:gap-x-[4.8125rem] md:gap-y-[6.4375rem]">
                            {benefits.map((benefit, index) => (
                                <div key={index}>
                                    <p className="m-0 font-sans font-normal text-white max-md:text-xl md:text-[2rem] md:leading-[2.625rem]">
                                        {benefit.title}
                                    </p>
                                    <div
                                        className={cn(
                                            'm-0 font-sans font-normal text-white/70 max-md:mt-2 max-md:text-sm md:mt-[0.4375rem] md:text-[1.25rem] md:leading-[1.3]',
                                            RICH_TEXT_HTML_CLASS,
                                        )}
                                        dangerouslySetInnerHTML={{
                                            __html: benefit.body,
                                        }}
                                    />
                                </div>
                            ))}
                        </div>

                        {/* The CTAs reuse the page's existing contact flows: "Book a
                    Meeting" opens the Client Director booking modal, "Send
                    Enquiry" opens the shared enquiry modal. The whole row keeps
                    the pre-existing enquiry gate: specifier-restricted
                    professionals show the login prompt instead, even when a
                    bookable session exists. */}
                        {showEnquiryCTA ? (
                            <div className="flex items-center gap-3 max-md:mt-8 max-md:w-full max-md:flex-col md:mt-[6.25rem] md:gap-4">
                                {canBookMeeting ? (
                                    <button
                                        type="button"
                                        onClick={() => setBookingOpen(true)}
                                        className={detailCta(
                                            'grey-dark',
                                            `bg-[#333] max-md:w-full ${CONTACT_CTA_SIZE}`,
                                        )}
                                    >
                                        Book a Meeting
                                    </button>
                                ) : null}
                                {showEnquiryCTA ? (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            openEnquiryModal({
                                                title: 'Send Enquiry',
                                                ctaLabel: 'Send Enquiry',
                                            })
                                        }
                                        className={detailCta(
                                            'outlined-dark',
                                            `border-white max-md:w-full ${CONTACT_CTA_SIZE}`,
                                        )}
                                    >
                                        Send Enquiry
                                    </button>
                                ) : null}
                            </div>
                        ) : showSpecifierLoginPrompt ? (
                            <div className="max-md:mt-8 md:mt-[4.9375rem]">
                                <SpecifierLoginPrompt
                                    professionalName={professional.Title}
                                    showBottomBorder={false}
                                    className="[&_*]:text-white!"
                                />
                            </div>
                        ) : null}
                    </div>

                    {/* Two or fewer images: the original static two-up. More than
                that: a full-width carousel, images kept at the same size. */}
                    {visibleCarousel.length > 2 ? (
                        <div className="max-md:mt-10 md:mt-[12rem]">
                            <Carousel
                                options={{
                                    align: 'start',
                                    containScroll: 'trimSnaps',
                                    dragFree: true,
                                    slidesToScroll: 'auto',
                                    breakpoints: {
                                        '(min-width: 768px)': {
                                            slidesToScroll: 1,
                                        },
                                    },
                                }}
                            >
                                <CarouselContent className="max-md:gap-4 md:gap-[1.875rem]">
                                    {visibleCarousel.map((image) => (
                                        <CarouselItem
                                            key={image.id}
                                            className="max-md:basis-[85%] md:basis-[calc(41.55%-0.9375rem)]"
                                        >
                                            <SectionImageTile image={image} />
                                        </CarouselItem>
                                    ))}
                                </CarouselContent>
                                <CarouselPagination />
                            </Carousel>
                        </div>
                    ) : visibleCarousel.length > 0 ? (
                        <div className="grid grid-cols-1 gap-[1.875rem] max-md:mt-10 md:mt-[12rem] md:ml-[0.8125rem] md:w-[83.1%] md:grid-cols-2">
                            {visibleCarousel.map((image) => (
                                <SectionImageTile
                                    key={image.id}
                                    image={image}
                                />
                            ))}
                        </div>
                    ) : null}

                    {/* Our Process — "Our Process" label on the left, numbered steps
                offset into the right column (Figma §9). */}
                    <div className="flex max-md:mt-12 max-md:flex-col md:mt-[9.375rem]">
                        <p className="m-0 shrink-0 font-sans font-normal text-white max-md:text-2xl md:w-[43.07%] md:pl-[1.9375rem] md:text-[2.625rem] md:leading-[1.2]">
                            {processHeading}
                        </p>
                        <div className="flex flex-col max-md:mt-8 md:-mt-[1.625rem] md:w-[56.93%]">
                            {steps.map((step, index) => (
                                <div
                                    key={index}
                                    className={cn(
                                        'flex items-center max-md:gap-5 max-md:py-3 md:py-[1.875rem]',
                                        index === 0 && 'md:pt-0',
                                    )}
                                >
                                    <span className="shrink-0 font-sans font-normal text-white max-md:text-6xl md:w-[18.5625rem] md:text-[12.5rem] md:leading-[11.25rem] md:tracking-[-0.125rem]">
                                        {stepNumber(index)}
                                    </span>
                                    <div
                                        className={cn(
                                            'm-0 font-sans font-normal text-white max-md:text-base md:text-[2rem] md:leading-[2.625rem]',
                                            RICH_TEXT_HTML_CLASS,
                                        )}
                                        dangerouslySetInnerHTML={{
                                            __html: step.body,
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Wide featured (hanging) image, right-offset, overhanging the
                dark section by ~24.5% of its height into the white block below
                (Figma §9). The negative bottom margin cancels the section's
                9.9rem bottom padding then pulls the image past the edge;
                `overflow-x-clip` + `z-10` let it spill down (not sideways) and
                paint over the trailing white spacer. */}
                    {featuredImage?.src ? (
                        <div className="max-md:mt-10 md:mt-[7.25rem] md:-mb-[20.65rem] md:ml-[42.36%] md:w-[56.93%]">
                            {featuredImage.link ? (
                                <RoccoLink
                                    to={featuredImage.link}
                                    className="bg-neutral-800 block aspect-[1051/702] overflow-hidden"
                                >
                                    <img
                                        src={featuredImage.src}
                                        alt={featuredImage.alt}
                                        loading="lazy"
                                        className="size-full object-cover object-bottom"
                                    />
                                </RoccoLink>
                            ) : (
                                <div className="bg-neutral-800 block aspect-[1051/702] overflow-hidden">
                                    <img
                                        src={featuredImage.src}
                                        alt={featuredImage.alt}
                                        loading="lazy"
                                        className="size-full object-cover object-bottom"
                                    />
                                </div>
                            )}
                        </div>
                    ) : null}
                </div>
            </section>

            {/* White breathing room equal to the image's overhang so the
                poke-out lands on white and the normal gap to the next section
                is preserved (desktop only — no overhang on mobile). */}
            {featuredImage?.src ? (
                <SectionSpacer
                    height={172}
                    mobileHeight={0}
                    className="bg-white"
                />
            ) : null}

            {canBookMeeting && bookingSession ? (
                <ClientDirectorBookingModal
                    sessionId={bookingSession.ID}
                    open={bookingOpen}
                    onOpenChange={setBookingOpen}
                />
            ) : null}
        </>
    );
};
